import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, {
  useContext,
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import { MODALS } from '../../../../config/constants/lightbox';
import { SCROLL_INLINE_CALENDAR } from '../../../../config/constants/scroll';
import { CYF_DEAL_TYPE } from '../../../../config/setup/setup';
import {
  BUY_EXCL,
  GIVE_AS_A_GIFT,
  VIEW_AVAILABILITY,
  GIFT_IT,
} from '../../../../config/text/text';
import { trackEvent } from '../../../../helpers/analytics';
import {
  isBookingCalendarChooseDateDeal,
  isBookingCalendarOnRedemptionDeal,
  isBookingCalendarOnRedemptionGiftDeal,
} from '../../../../helpers/deals';
import { isMobileViewport } from '../../../../helpers/screen';
import ThemeContext from '../../../../providers/ThemeProvider';
import WowModal from '../../../WowModal';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import { isNibbleEnabledDealLevel } from '../../../nibble/NibbleHelper';
import CalendarContainer from '../booking-calendar/CalendarContainerDynamic';
import CalendarOnRedemptionContainer from '../booking-calendar/CalendarOnRedemptionContainerDynamic';

const DealViewAvailabilityButton = forwardRef(
  (
    {
      buttonRef,
      deal,
      className,
      gift,
      condition,
      isGoogleHotel,
      isOptionSelected,
      isExperienceBox,
      mainDealGiftButton,
      searchParams,
      setIsCYOSelected,
      buyButton,
      isExpressDealPage,
      paymentData,
      isExpressPayLoaded,
      setIsExpressPayLoaded,
      isNibbleAppeared,
      setIsNibbleAppeared,
    },
    ref,
  ) => {
    const theme = useContext(ThemeContext);
    const [showModal, setShowModal] = useState(
      isGoogleHotel && isMobileViewport() && !gift,
    );
    const userVipStatus = useSelector(
      (state) => state.user.userprofile?.userVipStatus,
    );
    const isTravelPageDesktop = condition === SCROLL_INLINE_CALENDAR;
    const isBookingCalendarChooseDate = isBookingCalendarChooseDateDeal(deal);
    const isBookingCalendarOnRedemption = isBookingCalendarOnRedemptionDeal(
      deal,
    );
    const isBookingCalendarOnRedemptionGift = isBookingCalendarOnRedemptionGiftDeal(
      deal,
    );
    useEffect(() => {
      if (!showModal && isExpressPayLoaded) {
        setIsExpressPayLoaded(false);
        setIsNibbleAppeared(false);
      }
    }, [showModal]);

    const handleNonGiftAction = () => {
      trackEvent('click_buy');
    };
    const handleTravelPageDesktopAction = () => {
      if (isOptionSelected) {
        buttonRef?.current?.click();
      } else {
        scroller.scrollTo(SCROLL_INLINE_CALENDAR, {
          delay: 100,
          duration: 1_000,
          smooth: true,
        });
      }
    };

    const clickAction = async () => {
      if (!gift) {
        handleNonGiftAction();
      }

      if (isTravelPageDesktop) {
        handleTravelPageDesktopAction();
      } else {
        setShowModal(true);
      }
    };

    const buttonText = isBookingCalendarOnRedemption
      ? BUY_EXCL
      : VIEW_AVAILABILITY;
    const giftText = isExperienceBox ? GIFT_IT : GIVE_AS_A_GIFT;

    useImperativeHandle(ref, () => ({
      clickPrimaryButton: () => clickAction(),
    }));

    const enforceFocus = !isNibbleEnabledDealLevel(deal, userVipStatus);

    return (
      <>
        <PrimaryButton
          aria-label={gift ? giftText : buttonText}
          buttonRef={isTravelPageDesktop ? undefined : buttonRef}
          className={classNames(
            { 'size-large view-availability-button': !gift },
            { outline: gift },
            className,
          )}
          id="view-availability"
          onClick={clickAction}
        >
          {gift ? giftText : buttonText}
        </PrimaryButton>
        {condition !== SCROLL_INLINE_CALENDAR && (
          <WowModal
            ariaLabelledBy="view-availability"
            centered
            dialogId={MODALS.dealAvailability}
            enforceFocus={enforceFocus}
            extraClasses="view-availability-modal"
            setShowModal={setShowModal}
            showModal={showModal}
          >
            <h3 className="headline">{deal.headline}</h3>
            {((isBookingCalendarChooseDate &&
              !(isBookingCalendarOnRedemptionGift && gift)) ||
              deal?.display?.dynamicTravelDeal) && (
              <>
                <CalendarContainer
                  buyButton={buyButton}
                  deal={deal}
                  isCYF={deal.dealType === CYF_DEAL_TYPE}
                  isDynamicTravelDeal={deal?.display?.dynamicTravelDeal}
                  isExpressDealPage={isExpressDealPage}
                  isExpressPayLoaded={isExpressPayLoaded}
                  isGift={gift}
                  isGoogleHotel={isGoogleHotel}
                  isModal
                  isNibbleAppeared={isNibbleAppeared}
                  mainDealGiftButton={mainDealGiftButton}
                  paymentData={paymentData}
                  searchParams={searchParams}
                  setIsCYOSelected={setIsCYOSelected}
                  setIsExpressPayLoaded={setIsExpressPayLoaded}
                  setIsNibbleAppeared={setIsNibbleAppeared}
                />
              </>
            )}
            {(isBookingCalendarOnRedemption ||
              (isBookingCalendarOnRedemptionGift &&
                gift &&
                !deal?.display?.dynamicTravelDeal)) && (
              <CalendarOnRedemptionContainer
                deal={deal}
                isCalendarHidden
                isGift={gift}
                isModal
                setIsCYOSelected={setIsCYOSelected}
              />
            )}
          </WowModal>
        )}

        <style jsx>{`
          .headline {
            margin-bottom: 15px;
          }
          :global(.view-availability-modal .modal-dialog) {
            max-width: 600px;
          }
          :global(.view-availability-modal .modal-body) {
            padding: 0 20px 20px 20px;
          }

          @media (max-width: ${theme.breakpoints.smDown}) {
            :global(.view-availability-modal .modal-body) {
              padding: 0 4px 4px 4px;
            }
          }
        `}</style>
      </>
    );
  },
);

DealViewAvailabilityButton.propTypes = {
  buttonRef: PropTypes.object,
  className: PropTypes.string,
  condition: PropTypes.string,
  deal: PropTypes.object,
  gift: PropTypes.bool,
  isExperienceBox: PropTypes.bool,
  isGoogleHotel: PropTypes.bool,
  isOptionSelected: PropTypes.bool,
  mainDealGiftButton: PropTypes.object,
  searchParams: PropTypes.object,
  setIsCYOSelected: PropTypes.func,
};

DealViewAvailabilityButton.defaultProps = {
  buttonRef: null,
  className: '',
  condition: '',
  deal: null,
  gift: false,
  isExperienceBox: false,
  isGoogleHotel: false,
  isOptionSelected: false,
  mainDealGiftButton: null,
  searchParams: {},
  setIsCYOSelected: () => {},
};

DealViewAvailabilityButton.displayName = 'DealViewAvailabilityButton';

export default DealViewAvailabilityButton;
